import { LinearGradient } from 'expo-linear-gradient';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { Platform, StyleSheet, type ViewStyle } from 'react-native';
import { Box, Text, VStack } from '../../../components/primitives';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';

export type NodeType = 'trigger' | 'action' | 'condition' | 'output';

interface WorkflowNodeProps {
  type: NodeType;
  label: string;
  icon: ReactNode;
  isActive?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
}

const nodeColors: Record<
  NodeType,
  { bg: string; bgGradient: [string, string]; border: string; icon: string; glow: string }
> = {
  trigger: {
    bg: colors.primary.muted,
    bgGradient: [colors.primary.muted, colors.primary.faint],
    border: colors.primary.neon,
    icon: colors.primary.neon,
    glow: colors.glow.cyan,
  },
  action: {
    bg: colors.accent.purple.gradientStart,
    bgGradient: [colors.accent.purple.gradientStart, colors.accent.purple.gradientEnd],
    border: colors.accent.purple[500],
    icon: colors.accent.purple[400],
    glow: colors.glow.purple,
  },
  condition: {
    bg: colors.status.info.bg,
    bgGradient: [colors.status.info.gradientStart, colors.status.info.gradientEnd],
    border: colors.status.info.main,
    icon: colors.status.info.main,
    glow: colors.glow.info,
  },
  output: {
    bg: colors.status.success.bg,
    bgGradient: [colors.status.success.gradientStart, colors.status.success.gradientEnd],
    border: colors.status.success.main,
    icon: colors.status.success.main,
    glow: colors.glow.green,
  },
};

export function WorkflowNode({ type, label, icon, isActive = false }: Readonly<WorkflowNodeProps>) {
  const [isHovered, setIsHovered] = useState(false);
  const nodeStyle = nodeColors[type];

  const webHoverStyle = Platform.select({
    web: {
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      cursor: 'pointer',
      ...(isHovered && {
        transform: [{ scale: 1.05 }],
        boxShadow: `0 0 16px ${nodeStyle.glow}, 0 4px 8px rgba(0, 0, 0, 0.2)`,
      }),
    } as ViewStyle,
    default: {},
  });

  const hoverProps = Platform.select({
    web: {
      onMouseEnter: () => setIsHovered(true),
      onMouseLeave: () => setIsHovered(false),
    },
    default: {},
  });

  return (
    <VStack spacing="sm" align="center" style={styles.nodeColumn}>
      <Box
        borderRadius="lg"
        borderWidth={isActive || isHovered ? 2 : 1}
        borderColor={isActive || isHovered ? nodeStyle.border : colors.border.muted}
        overflow="hidden"
        style={[
          {
            width: 66,
            height: 66,
            shadowColor: isActive ? nodeStyle.border : 'transparent',
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: isActive ? 0.5 : 0,
            shadowRadius: 8,
            elevation: isActive ? 4 : 0,
          },
          webHoverStyle,
        ]}
        {...hoverProps}
      >
        <LinearGradient
          colors={nodeStyle.bgGradient}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={{
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            padding: tokens.spacing.md,
          }}
        >
          {icon}
        </LinearGradient>
      </Box>
      {label ? (
        <Text
          variant="caption"
          color={colors.text.secondary}
          style={styles.nodeLabel}
          numberOfLines={2}
        >
          {label}
        </Text>
      ) : null}
    </VStack>
  );
}

const styles = StyleSheet.create({
  nodeColumn: {
    width: 90,
    alignItems: 'center',
  },
  nodeLabel: {
    textAlign: 'center',
    maxWidth: 88,
    minHeight: 36,
    lineHeight: 18,
    fontWeight: '500',
  },
});
